import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Globe, ArrowsClockwise, Warning, Shield, Receipt, ClockCountdown, TrendUp, Lightning,
} from "@phosphor-icons/react";

function Stat({ label, value, sub, accent = "", testid }) {
  return (
    <Card className="p-6 border-border hover:border-foreground/20 transition-colors" data-testid={testid}>
      <div className="overline mb-3">{label}</div>
      <div className={`font-display text-4xl font-black tracking-tight ${accent}`}>{value}</div>
      {sub && <div className="mt-2 text-xs text-muted-foreground">{sub}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/domains/stats");
      setStats(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const refreshFromNamecheap = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/domains/refresh");
      toast.success(`Synced ${data.count} domains from Namecheap`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <Layout>
      <PageHeader
        eyebrow="Overview"
        title="Operations Dashboard"
        description="A live snapshot of your Namecheap portfolio — domain totals, renewals, DNS posture and recent activity."
        actions={
          <Button onClick={refreshFromNamecheap} disabled={refreshing} data-testid="refresh-btn">
            <ArrowsClockwise size={16} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Syncing…" : "Sync from Namecheap"}
          </Button>
        }
      />

      {loading ? (
        <div className="overline">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Stat testid="stat-total" label="Total Domains" value={stats?.total ?? 0}
                  sub={`${stats?.recent_added ?? 0} added in last 30 days`} />
            <Stat testid="stat-expiring" label="Expiring ≤ 30 days" value={stats?.expiring_soon ?? 0}
                  accent="text-warning" sub={`${stats?.expired ?? 0} already expired`} />
            <Stat testid="stat-custom-dns" label="Custom DNS" value={stats?.custom_dns ?? 0}
                  sub={`${stats?.namecheap_dns ?? 0} on Namecheap DNS`} />
            <Stat testid="stat-activity" label="Recent Changes"
                  value={(stats?.recent_dns_changes?.length ?? 0) + (stats?.recent_orders?.length ?? 0)}
                  sub="DNS updates & purchases (last 10 each)" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-6" data-testid="recent-dns-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="overline">Recent Activity</div>
                  <h3 className="font-display text-xl font-bold">DNS Changes</h3>
                </div>
                <Lightning size={20} className="text-muted-foreground" />
              </div>
              {stats?.recent_dns_changes?.length ? (
                <div className="divide-y divide-border">
                  {stats.recent_dns_changes.map((a) => (
                    <div key={a.id} className="py-3 flex items-start gap-3 text-sm">
                      <StatusBadge variant={a.status === "success" ? "success" : a.status === "partial" ? "warning" : "danger"}>
                        {a.status}
                      </StatusBadge>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-xs truncate">
                          {(a.target_domains || []).slice(0, 3).join(", ")}
                          {a.target_domains?.length > 3 && ` +${a.target_domains.length - 3}`}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {new Date(a.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-md">
                  No DNS changes yet. Run a bulk DNS update from the Domains page.
                </div>
              )}
            </Card>

            <Card className="p-6" data-testid="recent-orders-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="overline">Recent Activity</div>
                  <h3 className="font-display text-xl font-bold">Purchases</h3>
                </div>
                <Receipt size={20} className="text-muted-foreground" />
              </div>
              {stats?.recent_orders?.length ? (
                <div className="divide-y divide-border">
                  {stats.recent_orders.map((o) => (
                    <div key={o.order_id} className="py-3 flex items-start gap-3 text-sm">
                      <StatusBadge variant={o.purchase_status === "success" ? "success" : o.purchase_status === "partial" ? "warning" : "danger"}>
                        {o.purchase_status}
                      </StatusBadge>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-xs truncate">
                          {(o.domains || []).slice(0, 3).join(", ")}
                          {o.domains?.length > 3 && ` +${o.domains.length - 3}`}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          ${o.total_cost?.toFixed?.(2) ?? "0.00"} · {new Date(o.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-md">
                  No purchases yet. Try the AI name generator on the Buy Domains page.
                </div>
              )}
            </Card>
          </div>

          <Card className="p-6 mt-4 grid-lines-bg" data-testid="quick-actions-card">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="overline mb-2">Quick Start</div>
                <h3 className="font-display text-xl font-bold">Not seeing your domains?</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                  Configure your Namecheap API credentials in Settings, then sync.
                </p>
              </div>
              <div className="flex gap-2">
                <Link to="/settings"><Button variant="outline" data-testid="quick-settings">Go to Settings</Button></Link>
                <Link to="/domains"><Button data-testid="quick-domains">View Domains</Button></Link>
              </div>
            </div>
          </Card>
        </>
      )}
    </Layout>
  );
}
